import "rxjs/add/operator/delay";
import "rxjs/add/operator/do";
import "rxjs/add/operator/finally";
import "rxjs/add/observable/of";
import "rxjs/add/observable/fromPromise";
import "rxjs/add/operator/map";
import {IHttpService} from "angular";
import {IBasicDataFeed, IDatafeedQuotesApi, SubscribeBarsCallback} from "../../../../vendor/tradeview/charting_library";
import {getCompanies} from "../datafeeds/udf/src/helper";
import {
    ErrorCallback,
    HistoryCallback,
    LibrarySymbolInfo,
    Nominal,
    OnReadyCallback,
    PeriodParams,
    ResolutionString,
    ResolveCallback,
    SearchSymbolResultItem,
    SearchSymbolsCallback,
    SymbolResolveExtension
} from "../../../../vendor/tradeview/charting_library/datafeed-api";
import {ReplaySubject} from "rxjs";
import {TvHistoryProvider} from "./TvHistoryProvider";
import {GetBarsResult} from "../../../../vendor/tradeview/datafeeds/udf/src/history-provider";
import {TvDataPulseProvider} from "./TvDataPulseProvider";
import {CurrentUserService} from "../../../services/user/CurrentUserService";
import {MarketUtil, OutsideHoursUpdateInterval, PriceUpdateInterval} from "../../../js/util/MarketUtil";

export class TradingViewQuoteFeedService {

    public static id = "tradingViewQuoteFeedService";
    private tvHistoryProvider: TvHistoryProvider;
    private tvDataPulseProvider: TvDataPulseProvider;
    private readonly destroy$ = new ReplaySubject(1);
    private updateFrequency: number;

    /** @ngInject */
    public constructor(private $http: IHttpService,
                       tvHistoryProvider: TvHistoryProvider,
                       currentUserService: CurrentUserService) {
        this.tvHistoryProvider = tvHistoryProvider;
        this.updateFrequency = MarketUtil.extendedMarketIsOpen() ? PriceUpdateInterval : OutsideHoursUpdateInterval;
        this.tvDataPulseProvider = new TvDataPulseProvider(this.tvHistoryProvider, this.updateFrequency, currentUserService);
    }

    private async getAllCompanies() {
        const data = await getCompanies();
        const allSymbols = [];

        for (const company of data) {
            const symbol: SearchSymbolResultItem = {
                symbol: company.code,
                full_name: company.name,
                ticker: company.code + ":" + company.exchange,
                description: company.name,
                exchange: company.exchange,
                type: "all",
            };
            allSymbols.push(symbol);
        }
        return allSymbols;
    }

    public getDataFeed(): IBasicDataFeed | (IBasicDataFeed & IDatafeedQuotesApi) {
        const configurationData = {
            // Represents the resolutions for bars supported by your datafeed
            supported_resolutions: ["1", "3", "5", "10", "15", "30", "60", "1D", "1W", "1M"] as Nominal<string, "ResolutionString">[],
            // supported_resolutions: ["1", "3", "5", "10", "15", "30", "45", "60", "1D", "1W", "1M"] as Nominal<string, "ResolutionString">[],
            // The `exchanges` arguments are used for the `searchSymbols` method if a user selects the exchange
            exchanges: [
                { value: "All", name: "All", desc: "All"},
                { value: "ASX", name: "ASX", desc: "ASX"},
                { value: "AOM", name: "AOM", desc: "AOM"},
                { value: "AXW", name: "AXW", desc: "AXW"},
                { value: "NSX", name: "NSX", desc: "NSX"},
            ],
            // The `symbols_types` arguments are used for the `searchSymbols` method if a user selects this symbol type
            symbols_types: [
                { name: "All", value: "all"}
            ]
        };

        return {
            onReady(callback: OnReadyCallback) {
                    console.log("[onReady]: Method call");
                    setTimeout(() => callback(configurationData));
            },
            searchSymbols: async (userInput: string, exchange: string, symbolType: string, onResult: SearchSymbolsCallback) => {
                console.log("[searchSymbols]: Method call");
                const symbols = await this.getAllCompanies();
                const newSymbols = symbols.filter(symbol => {
                    const isExchangeValid = exchange === "All" || symbol.exchange === exchange;
                    // @ts-ignore
                    const isFullSymbolContainsInput = symbol.ticker
                        .toLowerCase()
                        .indexOf(userInput.toLowerCase()) !== -1;
                    return isExchangeValid && isFullSymbolContainsInput;
                });
                onResult(newSymbols);
            },
            resolveSymbol: async (symbolName: string, onResolve: ResolveCallback, onError: ErrorCallback, extension?: SymbolResolveExtension) => {
                const symbols = await this.getAllCompanies();
                const symbolItem = symbols.find(({ ticker, symbol }) => ticker === symbolName || symbol === symbolName );
                if (!symbolItem) {
                    console.log("[resolveSymbol]: Cannot resolve symbol", symbolName);
                    onError("Cannot resolve symbol");
                    return;
                }
                // Symbol information object
                const symbolInfo: LibrarySymbolInfo = {
                    ticker: symbolItem.ticker,
                    format: "price",
                    listed_exchange: symbolItem.exchange,
                    name: symbolItem.symbol,
                    description: symbolItem.description,
                    type: symbolItem.type,
                    session: "0930-1630:23456",
                    // session_holidays: holidays.join(","),
                    timezone: "Australia/Sydney",
                    exchange: symbolItem.exchange,
                    minmov: 1,
                    pricescale: 100,
                    has_intraday: true,
                    visible_plots_set: "ohlcv",
                    has_weekly_and_monthly: false,
                    supported_resolutions: configurationData.supported_resolutions,
                    volume_precision: 2,
                    data_status: "streaming",
                };
                onResolve(symbolInfo);
            },
            getBars: async (symbolInfo: LibrarySymbolInfo, resolution: ResolutionString, periodParams: PeriodParams, onResult: HistoryCallback, onError: ErrorCallback) => {
                this.tvHistoryProvider.getBars(symbolInfo, resolution, periodParams)
                    .then((result: GetBarsResult) => {
                        onResult(result.bars, result.meta);
                    })
                    .catch(onError);
            },
            subscribeBars: (symbolInfo: LibrarySymbolInfo, resolution: ResolutionString, onTick: SubscribeBarsCallback, listenerGuid: string, onResetCacheNeededCallback: () => void) => {
                this.tvDataPulseProvider.subscribeBars(symbolInfo, resolution, onTick, listenerGuid);
            },
            unsubscribeBars: (subscriberUID) => {
                console.log("[unsubscribeBars]: Method call with subscriberUID:", subscriberUID);
                this.tvDataPulseProvider.unsubscribeBars(subscriberUID);
            }
        };

    }

    public destroy() {
        {
            this.destroy$.next(true);
        }
    }
}
